import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, AlignLeft, Phone, Users, X, Navigation } from 'lucide-react';

const isoLocal = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const API = process.env.REACT_APP_BACKEND_URL || '';

const JOURS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const HEURE_DEBUT = 8;
const HEURE_FIN = 20;
const HAUTEUR_HEURE = 56;

const HORAIRES = {
  journee: [8.5, 19],
  matin: [8.5, 13],
  apres_midi: [14.5, 19]
};

const iso = (d) => isoLocal(d);

// Le chantier s'ouvre a partir de la veille, ou s'il est deja demarre / termine
const estAccessible = (c) => {
  if (!c) return false;
  if (c.etape && c.etape !== 'planifie') return true;
  if (!c.date_prevue) return false;
  const veille = new Date(c.date_prevue + 'T00:00:00');
  veille.setDate(veille.getDate() - 1);
  const auj = new Date();
  auj.setHours(0, 0, 0, 0);
  return auj >= veille;
};

const lundiDe = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(12, 0, 0, 0);
  return d;
};

const enHeures = (h) => {
  const heure = Math.floor(h);
  const min = Math.round((h - heure) * 60);
  return `${heure}h${min ? String(min).padStart(2, '0') : ''}`;
};

const TechPlanning = () => {
  const [chantiers, setChantiers] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [lundi, setLundi] = useState(lundiDe(new Date()));
  const [jourChoisi, setJourChoisi] = useState(iso(new Date()));
  const [estMobile, setEstMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 900);
  const [fiche, setFiche] = useState(null);
  const [navOuvert, setNavOuvert] = useState(false);
  const [bloque, setBloque] = useState(false);
  const [vue, setVue] = useState(() => localStorage.getItem('crm_vue_agenda') || 'semaine');

  const changerVue = (v) => { setVue(v); localStorage.setItem('crm_vue_agenda', v); };
  const navigate = useNavigate();
  const emplacement = useLocation();

  useEffect(() => {
    const r = () => setEstMobile(window.innerWidth < 900);
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  useEffect(() => {
    const vise = emplacement.state?.jour;
    if (vise) {
      setJourChoisi(vise);
      setLundi(lundiDe(new Date(vise + 'T12:00:00')));
      changerVue('jour');
    }
  }, [emplacement.state]);

  useEffect(() => {
    const vise = emplacement.state?.jour;
    if (vise) {
      setJourChoisi(vise);
      setLundi(lundiDe(new Date(vise + 'T12:00:00')));
      setVue('jour');
      localStorage.setItem('crm_vue_agenda', 'jour');
    }
  }, [emplacement.state]);

  useEffect(() => {
    axios.get(`${API}/api/chantiers/mes/liste`)
      .then((r) => setChantiers(r.data.data || []))
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  const semaine = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(lundi);
    d.setDate(d.getDate() + i);
    return d;
  });

  const chantiersDuJour = (jourIso) => chantiers.filter((c) => {
    if (!c.date_prevue) return false;
    const duree = Math.max(1, Math.min(c.duree_jours || 1, 5));
    const debut = new Date(c.date_prevue + 'T12:00:00');
    for (let i = 0; i < duree; i++) {
      const d = new Date(debut);
      d.setDate(d.getDate() + i);
      if (iso(d) === jourIso) return true;
    }
    return false;
  });

  const decaler = (n) => {
    const d = new Date(lundi);
    d.setDate(d.getDate() + n * 7);
    setLundi(d);
    if (estMobile) setJourChoisi(iso(d));
  };

  const aujourdhui = iso(new Date());
  const heures = Array.from({ length: HEURE_FIN - HEURE_DEBUT }, (_, i) => HEURE_DEBUT + i);
  const hauteurTotale = (HEURE_FIN - HEURE_DEBUT) * HAUTEUR_HEURE;
  const joursAffiches = vue === 'jour' ? [new Date(jourChoisi + 'T12:00:00')] : semaine;

  const carte = { background: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '14px' };

  if (chargement) return <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280' }}>Chargement…</div>;

  return (
    <div style={{ padding: '8px', maxWidth: estMobile ? '640px' : '100%', margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: '10px', padding: '3px', marginBottom: '12px' }}>
        {[['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois']].map(([cle, lib]) => (
          <button key={cle}
            onClick={() => {
              changerVue(cle);
              const maintenant = new Date();
              setLundi(lundiDe(maintenant));
              setJourChoisi(iso(maintenant));
            }}
            style={{
              flex: 1, padding: '9px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', border: 'none',
              background: vue === cle ? '#fff' : 'transparent',
              color: vue === cle ? '#065F46' : '#6B7280',
              fontWeight: vue === cle ? 500 : 400,
              boxShadow: vue === cle ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
            }}>
            {lib}
          </button>
        ))}
      </div>

      {vue === 'mois' ? (
        <div style={{ ...carte, padding: '14px' }}>
          {(() => {
            const ref = new Date(lundi);
            ref.setDate(ref.getDate() + 3);
            const an = ref.getFullYear(), mo = ref.getMonth();
            const premier = new Date(an, mo, 1);
            const debut = new Date(premier);
            debut.setDate(debut.getDate() - ((premier.getDay() + 6) % 7));
            const cases = Array.from({ length: 42 }, (_, i) => {
              const d = new Date(debut);
              d.setDate(d.getDate() + i);
              return d;
            });
            return (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', marginBottom: '6px' }}>
                  {JOURS.map((j) => (
                    <div key={j} style={{ textAlign: 'center', fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase' }}>{j[0]}</div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
                  {cases.map((d) => {
                    const cle = iso(d);
                    const duJour = chantiersDuJour(cle);
                    const horsMois = d.getMonth() !== mo;
                    const cestAujourdhui = cle === aujourdhui;
                    return (
                      <div key={cle} onClick={() => { setJourChoisi(cle); changerVue('jour'); }}
                        style={{ minHeight: '68px', padding: '4px', borderRadius: '6px', border: '1px solid #F3F4F6', cursor: 'pointer', opacity: horsMois ? 0.35 : 1 }}>
                        <div style={{
                          fontSize: '13px', textAlign: 'center', marginBottom: '3px',
                          width: '22px', height: '22px', lineHeight: '22px', margin: '0 auto 3px',
                          borderRadius: '50%',
                          background: cestAujourdhui ? '#10B981' : 'transparent',
                          color: cestAujourdhui ? '#fff' : '#374151'
                        }}>
                          {d.getDate()}
                        </div>
                        {duJour.slice(0, 2).map((c) => (
                          <div key={c.id} style={{ background: '#3D9B7C', color: '#fff', fontSize: '9px', padding: '2px 3px', borderRadius: '3px', marginBottom: '2px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            PV // {c.client?.nom?.toUpperCase() || ''}
                          </div>
                        ))}
                        {duJour.length > 2 && (
                          <div style={{ fontSize: '9px', color: '#9CA3AF', textAlign: 'center' }}>+{duJour.length - 2}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      ) : (
      <div style={{ ...carte, padding: '0', overflow: 'hidden' }}>
        {vue === 'semaine' && (
          <div style={{ display: 'grid', gridTemplateColumns: `54px repeat(${joursAffiches.length}, 1fr)`, borderBottom: '1px solid #F3F4F6' }}>
            <div />
            {joursAffiches.map((d, i) => {
              const cestAujourdhui = iso(d) === aujourdhui;
              return (
                <div key={iso(d)} onClick={() => { setJourChoisi(iso(d)); changerVue('jour'); }}
                  style={{ padding: '12px 4px', textAlign: 'center', borderLeft: '1px solid #F3F4F6', cursor: 'pointer' }}>
                  <div style={{ fontSize: '11px', color: cestAujourdhui ? '#10B981' : '#9CA3AF', textTransform: 'uppercase' }}>{JOURS[i]}</div>
                  <div style={{ fontSize: '18px', fontWeight: 500, color: cestAujourdhui ? '#10B981' : '#111827', marginTop: '2px' }}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>
        )}

        {vue === 'jour' && (
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', fontSize: '15px', fontWeight: 600, textTransform: 'capitalize' }}>
            {new Date(jourChoisi + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: `54px repeat(${joursAffiches.length}, 1fr)`, position: 'relative' }}>
          <div>
            {heures.map((h) => (
              <div key={h} style={{ height: `${HAUTEUR_HEURE}px`, fontSize: '11px', color: '#9CA3AF', textAlign: 'right', paddingRight: '8px', position: 'relative', top: '-7px', background: '#fff', paddingLeft: '4px' }}>
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {joursAffiches.map((d) => {
            const cle = iso(d);
            const duJour = chantiersDuJour(cle);
            return (
              <div key={cle} style={{ position: 'relative', borderLeft: '1px solid #F3F4F6', height: `${hauteurTotale}px` }}>
                {heures.map((h, i) => (
                  <div key={h} style={{ position: 'absolute', top: `${i * HAUTEUR_HEURE}px`, left: 0, right: 0, borderTop: '1px solid #F3F4F6' }} />
                ))}

                {duJour.map((c, k) => {
                  const [hDeb, hFin] = HORAIRES[c.creneau] || HORAIRES.journee;
                  const haut = (hDeb - HEURE_DEBUT) * HAUTEUR_HEURE;
                  const hauteur = (hFin - hDeb) * HAUTEUR_HEURE - 4;
                  const largeur = 100 / duJour.length;
                  return (
                    <div key={c.id} onClick={() => setFiche(c)}
                      style={{
                        position: 'absolute', top: `${haut}px`, height: `${hauteur}px`,
                        left: `${k * largeur}%`, width: `calc(${largeur}% - 6px)`, marginLeft: '3px',
                        background: '#3D9B7C', color: '#fff', borderRadius: '8px',
                        padding: '8px 10px', fontSize: '12px', cursor: 'pointer', overflow: 'hidden', lineHeight: 1.35
                      }}>
                      <div style={{ fontWeight: 600, marginBottom: '3px' }}>
                        PV // {c.client ? `${c.client.nom} ${c.client.prenom}`.toUpperCase() : 'CLIENT'}
                      </div>
                      <div style={{ opacity: 0.9 }}>De {enHeures(hDeb)} à {enHeures(hFin)}</div>
                      <div style={{ opacity: 0.9, marginTop: '3px' }}>
                        {estAccessible(c)
                          ? `${c.client?.adresse}, ${c.client?.code_postal} ${c.client?.ville}`
                          : `${c.client?.ville || ''} ${c.client?.code_postal || ''}`.trim()}
                      </div>
                      {hauteur > 130 && (
                        <div style={{ opacity: 0.85, marginTop: '8px', fontSize: '11px' }}>{c.etape_libelle}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {fiche && (() => {
        const [hD, hF] = HORAIRES[fiche.creneau] || HORAIRES.journee;
        const cl = fiche.client || {};
        const adresse = [cl.adresse, `${cl.code_postal || ''} ${cl.ville || ''}`.trim(), 'France'].filter(Boolean).join(', ');
        const jour = new Date(fiche.date_prevue + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        const micros = fiche.nb_panneaux ? Math.round(fiche.nb_panneaux / 2) : null;
        const desc = [
          fiche.puissance_kwc ? `INSTALLATION PANNEAUX ${fiche.puissance_kwc} KW` : null,
          fiche.nb_panneaux ? `${fiche.nb_panneaux} PANNEAUX` : null,
          micros ? `${micros} MICRO ONDULEUR` : null
        ].filter(Boolean).join(' - ');

        const ligne = { display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '20px' };
        const icone = { flexShrink: 0, marginTop: '2px' };

        return (
          <div onClick={() => { setFiche(null); setNavOuvert(false); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 95, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: '18px 18px 0 0', padding: '22px 20px 30px', width: '100%', maxWidth: '560px', maxHeight: '88vh', overflowY: 'auto' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '18px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#3D9B7C', flexShrink: 0, marginTop: '6px' }} />
                  <div>
                    <div style={{ fontSize: '21px', fontWeight: 600, lineHeight: 1.25 }}>
                      PV // {cl.nom ? `${cl.nom} ${cl.prenom}`.toUpperCase() : 'CLIENT'}
                      {cl.code_postal ? ` ${String(cl.code_postal).slice(0, 2)}` : ''}
                    </div>
                    <div style={{ fontSize: '15px', color: '#6B7280', marginTop: '5px', textTransform: 'capitalize' }}>
                      {jour} · De {enHeures(hD)} à {enHeures(hF)}
                    </div>
                  </div>
                </div>
                <X size={24} color="#6B7280" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => setFiche(null)} />
              </div>

              <div style={ligne}>
                <MapPin size={20} color="#6B7280" style={icone} />
                <div style={{ flex: 1 }}>
                  {estAccessible(fiche) ? (
                    <div onClick={() => setNavOuvert(true)}
                      style={{ fontSize: '16px', color: '#1D4ED8', cursor: 'pointer', textDecoration: 'underline' }}>
                      {adresse}
                    </div>
                  ) : (
                    <div style={{ fontSize: '16px', color: '#6B7280' }}>
                      {`${cl.ville || ''} ${cl.code_postal || ''}`.trim() || '—'}
                    </div>
                  )}
                </div>
              </div>

              {desc && (
                <div style={ligne}>
                  <AlignLeft size={20} color="#6B7280" style={icone} />
                  <div style={{ fontSize: '16px', lineHeight: 1.45 }}>{desc}</div>
                </div>
              )}

              {cl.telephone && estAccessible(fiche) && (
                <div style={ligne}>
                  <Phone size={20} color="#6B7280" style={icone} />
                  <a href={`tel:${cl.telephone}`} style={{ fontSize: '16px', color: '#1D4ED8', textDecoration: 'none' }}>
                    {cl.telephone}
                  </a>
                </div>
              )}

              <div style={ligne}>
                <Users size={20} color="#6B7280" style={icone} />
                <div style={{ fontSize: '16px' }}>{fiche.equipe?.nom || '—'}</div>
              </div>

              {fiche.commentaire && (
                <div style={{ padding: '12px', background: '#FFFBEB', borderRadius: '8px', fontSize: '14px', color: '#92400E', marginBottom: '20px' }}>
                  {fiche.commentaire}
                </div>
              )}

              <div style={{ height: '1px', background: '#F3F4F6', margin: '4px 0 18px' }} />

              <div style={{ fontSize: '14px', color: '#10B981', marginBottom: '16px' }}>{fiche.etape_libelle}</div>

              {estAccessible(fiche) ? (
                <button onClick={() => navigate('/ops/mes-chantiers', { state: { chantierId: fiche.id } })}
                  style={{ width: '100%', padding: '14px', border: 'none', borderRadius: '10px', background: '#10B981', color: '#fff', fontSize: '16px', cursor: 'pointer' }}>
                  Ouvrir le chantier
                </button>
              ) : (
                <>
                  <button onClick={() => setBloque(true)}
                    style={{ width: '100%', padding: '14px', border: '1px solid #E5E7EB', borderRadius: '10px', background: '#F9FAFB', color: '#9CA3AF', fontSize: '16px', cursor: 'pointer' }}>
                    Ouvrir le chantier
                  </button>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', marginTop: '8px' }}>
                    Accessible la veille de l'installation
                  </div>
                </>
              )}
            </div>

            {bloque && (
              <div onClick={(e) => { e.stopPropagation(); setBloque(false); }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div onClick={(e) => e.stopPropagation()}
                  style={{ background: '#fff', borderRadius: '14px', padding: '24px', width: '100%', maxWidth: '330px', textAlign: 'center' }}>
                  <div style={{ fontSize: '17px', fontWeight: 600, marginBottom: '8px' }}>Chantier pas encore ouvert</div>
                  <div style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5, marginBottom: '18px' }}>
                    Ce chantier sera accessible la veille de l'installation.
                  </div>
                  <button onClick={() => setBloque(false)}
                    style={{ width: '100%', padding: '13px', border: 'none', borderRadius: '10px', background: '#10B981', color: '#fff', fontSize: '16px', cursor: 'pointer' }}>
                    J'ai compris
                  </button>
                </div>
              </div>
            )}

            {navOuvert && (
              <div onClick={(e) => { e.stopPropagation(); setNavOuvert(false); }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div onClick={(e) => e.stopPropagation()}
                  style={{ background: '#fff', borderRadius: '14px', padding: '20px', width: '100%', maxWidth: '320px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '17px', fontWeight: 600, marginBottom: '16px' }}>
                    <Navigation size={18} color="#10B981" /> Ouvrir avec
                  </div>
                  {[
                    { nom: 'Waze', url: `https://waze.com/ul?q=${encodeURIComponent(adresse)}&navigate=yes` },
                    { nom: 'Google Maps', url: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(adresse)}` },
                    { nom: 'Plans (Apple)', url: `https://maps.apple.com/?daddr=${encodeURIComponent(adresse)}` }
                  ].map((a) => (
                    <a key={a.nom} href={a.url} target="_blank" rel="noreferrer"
                      onClick={() => setNavOuvert(false)}
                      style={{ display: 'block', padding: '14px', border: '1px solid #E5E7EB', borderRadius: '10px', marginBottom: '10px', fontSize: '16px', color: '#111827', textDecoration: 'none', textAlign: 'center' }}>
                      {a.nom}
                    </a>
                  ))}
                  <button onClick={() => setNavOuvert(false)}
                    style={{ width: '100%', padding: '12px', border: 'none', background: 'transparent', color: '#6B7280', fontSize: '15px', cursor: 'pointer' }}>
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default TechPlanning;
